import {
    PMStatus,
    type PMStatusInput,
    type LaborCostInput,
    type PMServiceLevel,
    type NextServiceInfo,
} from "../types/pmTypes";

/**
 * UM remaining before the target that counts as Due Soon.
 * A 500 interval at 10% is 50, including when the next due reading is 6000.
 */
export function dueSoonWindowUM(intervalUM: number, dueSoonPercentage: number): number {
    if (!(intervalUM > 0) || !(dueSoonPercentage > 0)) return 0;
    return intervalUM * dueSoonPercentage;
}

/**
 * Calculates the PM status for an enrolled unit based on UM counter values and thresholds.
 *
 * - Overdue: currentUM >= targetUM
 * - Due Soon: within dueSoonPercentage of the service interval (not of the target reading)
 * - Scheduled/OK: further out than that window
 *
 * Validates: Requirements 8.1, 8.2, 8.3
 */
export function calculatePMStatus(input: PMStatusInput): PMStatus {
    const { currentUM, targetUM, dueSoonPercentage } = input;

    if (currentUM >= targetUM) {
        return PMStatus.OVERDUE;
    }

    const intervalUM = input.intervalUM && input.intervalUM > 0 ? input.intervalUM : targetUM;
    const dueSoonThreshold = targetUM - dueSoonWindowUM(intervalUM, dueSoonPercentage);

    if (currentUM >= dueSoonThreshold) {
        return PMStatus.DUE_SOON;
    }

    return PMStatus.SCHEDULED;
}

/**
 * Calculates total labor cost from standard and overtime hours/rates.
 *
 * Formula: (laborHoursST * standardRate) + (laborHoursOT * overtimeRate)
 *
 * Validates: Requirement 6.6
 */
export function calculateLaborCost(input: LaborCostInput): number {
    return (input.laborHoursST * input.standardRate) + (input.laborHoursOT * input.overtimeRate);
}

/** Relative slack for float division when checking integer multiples of PM intervals. */
const PM_NEST_RATIO_EPSILON = 1e-9;

/**
 * True when `outerInterval` is an integer multiple of `innerInterval`
 * (e.g. 1000 nests 500; 1500 nests 500 but not 1000).
 *
 * Uses division + relative epsilon (not `%`) so values like 1500/500 stay exact
 * under float noise. Intervals are positive meter readings from admin config
 * (typically whole numbers); fractional intervals are not a supported product input.
 */
export function pmIntervalNestsInto(outerInterval: number, innerInterval: number): boolean {
    if (!(outerInterval > 0) || !(innerInterval > 0)) return false;
    if (innerInterval > outerInterval) return false;
    const ratio = outerInterval / innerInterval;
    const absRatio = Math.abs(ratio);
    return Math.abs(ratio - Math.round(ratio)) < PM_NEST_RATIO_EPSILON * Math.max(1, absRatio);
}

/**
 * Level 1 is the most frequent service: the configured level with the smallest
 * positive interval. Ties keep the first matching level.
 */
export function getPrimaryServiceLevel(
    serviceLevels: PMServiceLevel[],
): PMServiceLevel | null {
    const valid = serviceLevels.filter((sl) => sl.intervalValue > 0);
    if (valid.length === 0) {
        return null;
    }
    return valid.reduce((smallest, sl) =>
        sl.intervalValue < smallest.intervalValue ? sl : smallest,
    );
}

/**
 * Computes the next due service point for a single level relative to its last actual.
 *
 * - Never serviced (null/0/negative lastServiceUM): next due = the interval itself
 * - Otherwise: last actual + interval (do not snap to a multiple of the interval)
 *
 * Example: interval=500, lastServiceUM=1017 → nextDue=1517
 * Example: interval=500, lastServiceUM=17500 → nextDue=18000
 */
export function computeNextDueForLevel(
    intervalValue: number,
    lastServiceUM: number | null | undefined,
): number {
    if (intervalValue <= 0) {
        return 0;
    }
    const lastUM = lastServiceUM ?? 0;
    if (lastUM <= 0) {
        return intervalValue;
    }
    return lastUM + intervalValue;
}

/**
 * Last-service baselines are stored as the face reading entered at service.
 * PM status compares the scheduling total (face + replaced meters). Add the
 * same replaced total to every baseline so a standing replaced offset does
 * not move the unit toward Overdue.
 *
 * A level with no last service is due at replaced + interval, which matches
 * comparing the face alone to the interval.
 */
export function serviceLevelsOnSchedulingScale<T extends { lastServiceUM?: number | null }>(
    levels: T[],
    replacedUM: number | null | undefined,
): T[] {
    const replaced = replacedUM != null && Number.isFinite(replacedUM) && replacedUM > 0
        ? replacedUM
        : 0;
    if (replaced === 0) return levels;

    return levels.map((level) => {
        const last = level.lastServiceUM ?? 0;
        const baseline = last > 0 ? last + replaced : replaced;
        return { ...level, lastServiceUM: baseline };
    });
}

/**
 * Returns the highest applicable service level when multiple levels are due simultaneously.
 * "Highest" means the level with the largest intervalValue among those where currentUM
 * has reached that level's last-actual + interval due point.
 * Returns null if no service levels are due.
 *
 * Validates: Requirement 3.6
 */
export function getHighestDueServiceLevel(
    serviceLevels: PMServiceLevel[],
    currentUM: number,
): PMServiceLevel | null {
    const dueLevels = serviceLevels
        .filter((sl) => {
            const nextDue = computeNextDueForLevel(sl.intervalValue, sl.lastServiceUM);
            return nextDue > 0 && currentUM >= nextDue;
        })
        .sort((a, b) => b.intervalValue - a.intervalValue);

    return dueLevels[0] || null;
}

/**
 * Calculates the remaining units of measure before the next service is due.
 * Returns 0 if the unit is already at or past the target.
 */
export function calculateRemainingUM(currentUM: number, targetUM: number): number {
    return Math.max(0, targetUM - currentUM);
}

/**
 * Next service target for the unit: last actual on L1 + L1 interval.
 * currentUM is unused — overdue units stay at the first missed point instead of
 * skipping forward to a later multiple.
 *
 * Validates: Requirement 2.1
 */
export function computeNextServicePoint(
    _currentUM: number,
    serviceLevels: PMServiceLevel[],
): number {
    const primary = getPrimaryServiceLevel(serviceLevels);
    if (!primary) {
        return 0;
    }
    return computeNextDueForLevel(primary.intervalValue, primary.lastServiceUM);
}

/**
 * Service levels that cascade into the next service point: each level whose own
 * next due (last actual + interval) is at or before the L1 target.
 *
 * Validates: Requirement 2.2
 */
export function getDueLevelsAtPoint(
    targetUM: number,
    serviceLevels: PMServiceLevel[],
): PMServiceLevel[] {
    if (targetUM === 0) {
        return [];
    }

    return serviceLevels
        .filter((sl) => {
            const nextDue = computeNextDueForLevel(sl.intervalValue, sl.lastServiceUM);
            return nextDue > 0 && nextDue <= targetUM;
        })
        .sort((a, b) => a.intervalValue - b.intervalValue);
}

/**
 * Next service for a unit: L1 last-actual + L1 interval, plus every level that
 * is due at or before that point (cascade).
 *
 * Returns null if serviceLevels is empty.
 *
 * Validates: Requirements 2.1, 2.2, 2.3
 */
export function getNextServiceInfo(
    currentUM: number,
    serviceLevels: PMServiceLevel[],
): NextServiceInfo | null {
    if (serviceLevels.length === 0) {
        return null;
    }

    const targetUM = computeNextServicePoint(currentUM, serviceLevels);
    const dueLevels = getDueLevelsAtPoint(targetUM, serviceLevels);

    return { targetUM, dueLevels };
}
